import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const POLL_INTERVAL = 2000;
const POLL_MAX_TRIES = 10;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ payment_status: "pending" });
  const [tries, setTries] = useState(0);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async (attempt) => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setStatus(data);
        setTries(attempt);
        if (data.payment_status === "paid") {
          refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) return;
        if (attempt >= POLL_MAX_TRIES) { setTimedOut(true); return; }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      } catch (_e) {
        if (attempt >= POLL_MAX_TRIES) setTimedOut(true);
        else setTimeout(() => poll(attempt + 1), POLL_INTERVAL);
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const paid = status.payment_status === "paid";
  const failed = ["failed", "expired"].includes(status.payment_status);

  return (
    <Layout>
      <div data-testid="payment-success-page" className="max-w-lg mx-auto text-center py-16">
        {paid ? (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">— Welcome to Pro</p>
            <h1 className="font-head text-4xl text-[#EBE5D9] mb-4">You&apos;re in.</h1>
            <p className="text-[#A39A8E] mb-8">
              Every trade you journal from here on now has the AI coach fully at your side. Consistency compounds.
            </p>
            <button data-testid="go-to-today" onClick={() => nav("/today")} className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-6 py-3 rounded-sm hover:bg-[#EBE5D9] transition-colors font-mono text-xs uppercase tracking-widest">
              Start journaling
            </button>
          </>
        ) : failed ? (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#B56258] mb-2">— Payment {status.payment_status}</p>
            <h1 className="font-head text-3xl text-[#EBE5D9] mb-4">Something didn&apos;t go through.</h1>
            <p className="text-[#A39A8E] mb-6">No charge was made. Try again from the pricing page.</p>
            <button onClick={() => nav("/pricing")} className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-6 py-2.5 rounded-sm hover:bg-[#EBE5D9] font-mono text-xs uppercase tracking-widest">Back to pricing</button>
          </>
        ) : timedOut ? (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">— Still processing</p>
            <h1 className="font-head text-3xl text-[#EBE5D9] mb-4">Give it a minute.</h1>
            <p className="text-[#A39A8E] mb-6 text-sm">
              Stripe is still confirming your payment. Refresh in a moment or head to Today — your Pro status will apply as soon as it lands.
            </p>
            <button onClick={() => window.location.reload()} className="bg-transparent border border-[#CFA66A] text-[#CFA66A] hover:bg-[#CFA66A]/10 px-6 py-2.5 rounded-sm font-mono text-xs uppercase tracking-widest">Refresh</button>
          </>
        ) : (
          <>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">— Confirming your payment</p>
            <div className="mt-6 flex items-center justify-center gap-2 font-mono text-xs text-[#A39A8E]">
              <span className="inline-block w-2 h-2 bg-[#CFA66A] animate-pulse" />
              <span>Checking with Stripe… ({tries}/{POLL_MAX_TRIES})</span>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
